import type { AbstractLeadFormProps, AbstractLeadFormState } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import { AbstractLeadForm } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import type { AbstractLeadFormFields, ProductType } from "@component/assets/AbstractLeadForm/types";
import type { EventComponentType } from "@utils/google-analytics-4.type";
import { logError } from "@utils/sentry-error";
import autobind from "autobind-decorator";
import { generateUrlParams } from "bm-ui-kit";
import type { ReactNode } from "react";
import React from "react";
import type { CorporateBankAccountSearchParams } from "../Calculator/types";
import type { SmeAccountLeadCalcRequest, SmeAccountLeadCalcResponse } from "../LeadForm/types";
import * as Step from "./Steps";
import "./style.scss";

export type CorporateBankAccountSmallCalculatorFormFields = CorporateBankAccountSearchParams & AbstractLeadFormFields;

export type CorporateBankAccountSmallCalculatorProps = AbstractLeadFormProps<
	SmeAccountLeadCalcRequest,
	SmeAccountLeadCalcResponse,
	CorporateBankAccountSmallCalculatorFormFields
>;

/**
 * @Shortcode
 *
 * @title "Vállalati bankszámla kalkulátor"
 * @shortcode "kkv_bankaccounts_small"
 * @slug "vallalati-bankszamla"
 * @type "calculator"
 */
export class CorporateBankAccountSmallCalculator<
	Props extends CorporateBankAccountSmallCalculatorProps = CorporateBankAccountSmallCalculatorProps,
> extends AbstractLeadForm<
	SmeAccountLeadCalcRequest,
	SmeAccountLeadCalcResponse,
	CorporateBankAccountSmallCalculatorFormFields,
	Props
> {
	// EventParams
	protected readonly productTypes: string = "SME_ACCOUNT";
	protected readonly componentType: EventComponentType = "small-calculator";

	public static defaultProps = {
		title: "Kérd szakértőnk segítségét!",
		outsideTitle: "KKV bankszámla kalkulátor",
		outsideTitleLink: "/vallalati/bankszamla/",
		bottomLogo: true,
	};

	private readonly targetUrl = "/vallalati/bankszamla/";

	protected getInitialState(): AbstractLeadFormState<CorporateBankAccountSmallCalculatorFormFields> {
		return {
			...super.getInitialState(),
			activeStep: "step-1",
			values: this.initValues(),
		};
	}

	protected initValues(): CorporateBankAccountSmallCalculatorFormFields {
		return {
			outgoingTransfer: undefined,
			// fake
			email: undefined,
			firstName: undefined,
			lastName: undefined,
			cityName: undefined,
			phone: undefined,
		};
	}

	protected renderSteps(): ReactNode {
		const {
			values: { outgoingTransfer },
		} = this.state;

		return (
			<>
				<Step.Step1 stepName="step-1" defaultTarget="result-pre" outgoingTransfer={outgoingTransfer} />
				<Step.Result stepName="result" canStepBack />
			</>
		);
	}

	protected onStep(target: string, withEvent: boolean): void {
		const { values } = this.state;

		if (target === "result-pre") {
			this.setState({
				submitState: "loading",
			});

			this.promisePool
				.add(this.onSubmit(values, false))
				.then(() => {
					this.setState({
						submitState: "done",
					});
				})
				.catch((error: Error) => {
					this.setState({
						submitState: "error",
					});

					logError(error);
				})
				.finally(() => {
					this.promisePool.addTimeout().then(() => {
						this.setState({
							submitState: undefined,
						});
					});
				});
		} else {
			super.onStep(target, withEvent);
		}
	}

	@autobind
	protected async onSubmit(
		searchParams: CorporateBankAccountSmallCalculatorFormFields,
		_isSort: boolean,
	): Promise<void> {
		window.open(`${this.targetUrl}?renderCalculatorResult=true&${generateUrlParams(searchParams)}`, "_blank");

		return Promise.resolve();
	}

	protected getAutoresponderFields(): Record<string, any> {
		return {};
	}

	protected getAutoresponderName(): string {
		return "";
	}

	protected getProductType(): ProductType {
		return "SME_ACCOUNT";
	}

	protected getComponentName(): string {
		return "CorporateBankAccountSmallCalculator";
	}
}

export default CorporateBankAccountSmallCalculator;
